import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/shared/EmptyState';
import type { MetricsLlm } from '@/services/adminMetricsService';

interface LlmUnitCardProps {
  data: MetricsLlm;
}

function formatPct(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}

/**
 * LLM unit-economics tile (F130): average cost per production message,
 * prompt-cache hit ratio, tier split, and per-tenant spend outliers (§11.5).
 * Mirrors MarginCard's monochrome `dl` grid + "aprox" badge convention.
 *
 * `cache_hit_ratio: null` means "not reported" (F039 — provider omitted
 * `cached_tokens_in`) and must render as "sem dados", never as 0%.
 */
export function LlmUnitCard({ data }: LlmUnitCardProps) {
  const { t } = useTranslation();

  const tierTotal = data.tier_split.fast + data.tier_split.capable;
  const fastPct = tierTotal > 0 ? Math.round((data.tier_split.fast / tierTotal) * 100) : 0;
  const capablePct = tierTotal > 0 ? 100 - fastPct : 0;

  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-muted-foreground">{t('admin.metrics.llm.cost_per_message')}</dt>
          <dd data-testid="llm-cost-per-message" className="mt-1 font-semibold tabular-nums">
            {data.cost_per_message_usd != null ? (
              <>
                ${data.cost_per_message_usd} USD
                <span className="ml-1.5">
                  <Badge variant="outline" className="text-xs">
                    {t('admin.metrics.llm.aprox')}
                  </Badge>
                </span>
              </>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </dd>
        </div>

        <div>
          <dt className="text-muted-foreground">{t('admin.metrics.llm.messages_count')}</dt>
          <dd data-testid="llm-messages-count" className="mt-1 text-xl font-bold tabular-nums">
            {data.messages}
          </dd>
        </div>

        <div>
          <dt className="text-muted-foreground">{t('admin.metrics.llm.cache_hit_ratio')}</dt>
          <dd data-testid="llm-cache-hit-ratio" className="mt-1 text-xl font-bold tabular-nums">
            {data.cache_hit_ratio != null ? (
              formatPct(data.cache_hit_ratio)
            ) : (
              <>
                <span className="text-base font-normal text-muted-foreground">—</span>
                <div className="text-xs font-normal text-muted-foreground">
                  {t('admin.metrics.llm.cache_no_data')}
                </div>
              </>
            )}
          </dd>
        </div>

        {tierTotal > 0 && (
          <div className="col-span-full" data-testid="llm-tier-split">
            <dt className="text-muted-foreground">{t('admin.metrics.llm.tier_split')}</dt>
            <dd className="mt-1 text-sm tabular-nums">
              {t('admin.metrics.llm.tier_fast')} {fastPct}% · {t('admin.metrics.llm.tier_capable')}{' '}
              {capablePct}%
            </dd>
          </div>
        )}
      </dl>

      <div>
        <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('admin.metrics.llm.outliers_title')}
        </h4>
        {data.tenant_outliers.length === 0 ? (
          <div data-testid="llm-outliers-empty">
            <EmptyState
              icon={AlertTriangle}
              title={t('admin.metrics.llm.outliers_empty_title')}
              subtitle={t('admin.metrics.llm.outliers_empty_body')}
            />
          </div>
        ) : (
          <ul data-testid="llm-outliers-list" className="space-y-2">
            {data.tenant_outliers.map((outlier) => (
              <li
                key={outlier.tenant_id}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <span className="min-w-0 truncate font-medium">{outlier.slug}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  ${outlier.cost_usd} USD · {outlier.ratio.toFixed(1)}×{' '}
                  {t('admin.metrics.llm.outlier_avg_suffix')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
